import { supabase } from './supabaseClient';

interface ApiResponse<T = any> {
  data?: T;
  error?: string;
  message?: string;
}

interface AuthResponse {
  access_token: string;
  token_type: string;
  expires_in?: number;
}

class ApiClient {
  private baseUrl: string;
  private backendToken: string | null = null;
  private tokenExpiry: number | null = null;
  private authMode: string;
  private authPromise: Promise<string> | null = null;
  private requestCache: Map<string, { data: any; timestamp: number }> = new Map();
  private cacheDuration: number = 30000; // 30 seconds default cache

  constructor() {
    this.baseUrl = import.meta.env.VITE_API_BASE_URL || 'https://admin-api.joulescrm.com';
    this.authMode = import.meta.env.VITE_AUTH_MODE || 'supabase';
  }

  /**
   * Authenticate with the backend API using the configured auth mode
   */
  private async authenticateWithBackend(): Promise<string> {
    // Check if we have a valid token
    if (this.backendToken && this.tokenExpiry && Date.now() < this.tokenExpiry) {
      return this.backendToken;
    }

    // If authentication is already in progress, wait for it
    if (this.authPromise) {
      return this.authPromise;
    }

    // Start new authentication process
    this.authPromise = this.performAuthentication();
    
    try {
      const token = await this.authPromise;
      this.authPromise = null; // Clear the promise after completion
      return token;
    } catch (error) {
      this.authPromise = null; // Clear the promise on error
      throw error;
    }
  }

  /**
   * Perform the actual authentication
   */
  private async performAuthentication(): Promise<string> {
    try {
      if (this.authMode === 'hardcoded') {
        return await this.authenticateWithHardcodedCredentials();
      } else if (this.authMode === 'supabase') {
        return await this.authenticateWithSupabaseSession();
      } else if (this.authMode === 'jwt_exchange') {
        return await this.authenticateWithJwtExchange();
      } else {
        // Default to Supabase session
        return await this.authenticateWithSupabaseSession();
      }
    } catch (error) {
      console.error('Backend authentication failed:', error);
      throw new Error('Failed to authenticate with backend API');
    }
  }

  /**
   * Authenticate using hardcoded credentials (for testing/development)
   */
  private async authenticateWithHardcodedCredentials(): Promise<string> {
    const email = import.meta.env.VITE_SUPER_ADMIN_EMAIL;
    const password = import.meta.env.VITE_SUPER_ADMIN_PASSWORD;

    if (!email || !password) {
      throw new Error('Hardcoded credentials not configured in environment');
    }

    // Use OAuth2 form data format for the login endpoint
    const formData = new URLSearchParams();
    formData.append('username', email);
    formData.append('password', password);

    const response = await fetch(`${this.baseUrl}/api/v1/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Authentication failed: ${response.status} ${errorText}`);
    }

    const authData: AuthResponse = await response.json();
    
    // Store token with expiry (backend tokens expire in 30 minutes)
    this.backendToken = authData.access_token;
    this.tokenExpiry = Date.now() + (25 * 60 * 1000); // Cache for 25 minutes to be safe

    return this.backendToken;
  }

  /**
   * Authenticate using current Supabase session
   */
  private async authenticateWithSupabaseSession(): Promise<string> {
    const { data: { session } } = await supabase.auth.getSession();

    if (!session?.access_token) {
      throw new Error('No active Supabase session found. Please log in.');
    }

    // Try user-level token exchange first (works for all authenticated users),
    // fall back to superadmin-only endpoint for backward compatibility
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/auth/token-exchange/user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          supabase_token: session.access_token
        })
      });

      if (response.ok) {
        const authData: AuthResponse = await response.json();
        this.backendToken = authData.access_token;
        this.tokenExpiry = Date.now() + (25 * 60 * 1000);
        return this.backendToken;
      }

      // If the user endpoint doesn't exist (404) or fails, fall back to superadmin endpoint
      if (response.status !== 404) {
        const errorText = await response.text();
        if (response.status === 401) {
          throw new Error('Authentication failed: Invalid Supabase session. Please log in again.');
        }
        // For other errors, try the fallback
        console.warn(`User token exchange returned ${response.status}, trying superadmin endpoint`);
      }

      // Fallback to superadmin-only token exchange
      const fallbackResponse = await fetch(`${this.baseUrl}/api/v1/auth/token-exchange`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          supabase_token: session.access_token
        })
      });

      if (!fallbackResponse.ok) {
        const errorText = await fallbackResponse.text();
        if (fallbackResponse.status === 403) {
          throw new Error('Access denied: Super admin privileges required for backend API access');
        } else if (fallbackResponse.status === 401) {
          throw new Error('Authentication failed: Invalid Supabase session. Please log in again.');
        } else {
          throw new Error(`Token exchange failed: ${fallbackResponse.status} ${errorText}`);
        }
      }

      const authData: AuthResponse = await fallbackResponse.json();

      this.backendToken = authData.access_token;
      this.tokenExpiry = Date.now() + (25 * 60 * 1000);

      return this.backendToken;
    } catch (error) {
      console.error('Supabase token exchange failed:', error);
      throw error;
    }
  }


  /**
   * Authenticate using JWT exchange (same as Supabase session - for backward compatibility)
   */
  private async authenticateWithJwtExchange(): Promise<string> {
    return await this.authenticateWithSupabaseSession();
  }

  /**
   * Make authenticated HTTP request
   */
  private async makeRequest<T>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<T> {
    try {
      const token = await this.authenticateWithBackend();
      
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          ...options.headers,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        
        // If token expired, clear it and retry once
        if (response.status === 401 && this.backendToken) {
          this.backendToken = null;
          this.tokenExpiry = null;
          
          // Retry with fresh token
          const newToken = await this.authenticateWithBackend();
          const retryResponse = await fetch(`${this.baseUrl}${endpoint}`, {
            ...options,
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${newToken}`,
              ...options.headers,
            },
          });

          if (!retryResponse.ok) {
            const retryErrorText = await retryResponse.text();
            throw new Error(`API request failed: ${retryResponse.status} ${retryErrorText}`);
          }

          return await retryResponse.json();
        }
        
        throw new Error(`API request failed: ${response.status} ${errorText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('API request error:', error);
      throw error;
    }
  }

  /**
   * GET request with caching
   */
  async get<T>(endpoint: string, params?: Record<string, any>, skipCache: boolean = false): Promise<T> {
    const url = new URL(endpoint, this.baseUrl);
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, String(value));
        }
      });
    }

    const cacheKey = url.pathname + url.search;
    
    // Check cache first (unless explicitly skipped)
    if (!skipCache) {
      const cached = this.requestCache.get(cacheKey);
      if (cached && (Date.now() - cached.timestamp) < this.cacheDuration) {
        return cached.data as T;
      }
    }

    // Make the request
    const data = await this.makeRequest<T>(cacheKey);
    
    // Cache the result
    this.requestCache.set(cacheKey, {
      data,
      timestamp: Date.now()
    });
    
    // Clean old cache entries
    this.cleanCache();
    
    return data;
  }

  /**
   * Clean expired cache entries
   */
  private cleanCache() {
    const now = Date.now();
    for (const [key, value] of this.requestCache.entries()) {
      if (now - value.timestamp > this.cacheDuration) {
        this.requestCache.delete(key);
      }
    }
  }

  /**
   * Clear all cached requests
   */
  clearCache() {
    this.requestCache.clear();
  }

  /**
   * Clear cache for specific endpoint pattern
   */
  clearCacheForEndpoint(endpointPattern: string) {
    for (const key of this.requestCache.keys()) {
      if (key.includes(endpointPattern)) {
        this.requestCache.delete(key);
      }
    }
  }

  /**
   * POST request
   */
  async post<T>(endpoint: string, data?: any): Promise<T> {
    return this.makeRequest<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * POST request with FormData
   */
  async postFormData<T>(endpoint: string, formData: FormData): Promise<T> {
    try {
      const token = await this.authenticateWithBackend();
      
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          // Don't set Content-Type for FormData, let browser set it with boundary
        },
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API request failed: ${response.status} ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('FormData API request error:', error);
      throw error;
    }
  }

  /**
   * PUT request
   */
  async put<T>(endpoint: string, data?: any): Promise<T> {
    return this.makeRequest<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string): Promise<T> {
    return this.makeRequest<T>(endpoint, {
      method: 'DELETE',
    });
  }

  /**
   * Check if user is authenticated with backend
   */
  async isAuthenticated(): Promise<boolean> {
    try {
      await this.authenticateWithBackend();
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Clear stored authentication
   */
  clearAuth(): void {
    this.backendToken = null;
    this.tokenExpiry = null;
  }

  /**
   * Get current auth mode
   */
  getAuthMode(): string {
    return this.authMode;
  }
}

// Export singleton instance
export const apiClient = new ApiClient();